import { useState } from "react";
import { useHistory } from "react-router";
import type { Passport, Person } from "@b2bportal/air-booking-api";
import {
  DesktopLayout,
  FlightPassengerSelectors,
  getChildState,
  MobileLayout,
  ParentState,
  PassportSelectors,
  useCheckoutState,
  useChildMachineState,
  useCheckoutStateSelector as useSelector,
} from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  B2BLoadingPopup,
  BackButton,
  ErrorModal,
  MobileFloatingButton,
  PassportInfoForm,
} from "@hopper-b2b/ui";
import { useDeviceTypes } from "@hopper-b2b/utilities";
import clsx from "clsx";

import Error from "../../../../assets/client/error.svg";
import { PATH_FLIGHTS_SEARCH } from "../../../../utils/urlPaths";
import { Event, type TEvent } from "../../events";
import {
  getClientAssets,
  getFlightSearchDepartureDate,
  getFlightSearchReturnDate,
} from "../../selectors";
import { type FlightMachineContext, PassportState } from "../../types";
import { ConnectedPriceBreakdownContent } from "../PriceBreakdownDropdown";
import "./styles.scss";

const passengerChildMachineID = ParentState.passengerInformation;

export const PassportInformation = () => {
  const history = useHistory();
  const { matchesMobile } = useDeviceTypes();
  const { t } = useI18nContext();
  const [state, send] = useCheckoutState<TEvent, FlightMachineContext>();
  const [, sendToPassengerMachine] = useChildMachineState<
    TEvent,
    FlightMachineContext
  >(passengerChildMachineID);
  const childState = getChildState(state.value);

  const selectedPassengers = useSelector(
    FlightPassengerSelectors.getAllSelectedUserPassengersParent
  );
  const isLoading = useSelector(PassportSelectors.getIsPassportLoading);
  const passportError = useSelector(PassportSelectors.getPassportError);
  const clientAssets = useSelector(getClientAssets);
  const departureDate = useSelector(getFlightSearchDepartureDate);
  const returnDate = useSelector(getFlightSearchReturnDate);

  const [userIndex, setUserIndex] = useState(0);
  const [passportInfo, setPassportInfo] = useState<Passport | undefined>(
    selectedPassengers[userIndex]?.passport || undefined
  );
  const [passportExpiryErrorMessage, setPassportExpiryErrorMessage] =
    useState("");
  const [passportNumberErrorMessage, setPassportNumberErrorMessage] =
    useState("");

  const handleGoBack = () => history.goBack();

  const updatePassengerWithPassport = (person: Person, onContinue?: boolean) =>
    send({
      type: onContinue
        ? Event.UPDATE_PASSPORT_AND_CONTINUE
        : Event.UPDATE_PASSENGER,
      person,
    });

  const isDisabled = () => {
    return (
      !!passportExpiryErrorMessage ||
      !!passportNumberErrorMessage ||
      !passportInfo ||
      !passportInfo?.countryOfIssue ||
      !passportInfo?.expiration ||
      !passportInfo?.number
    );
  };

  const handleAddPassportInfo = () => {
    const person = selectedPassengers[userIndex];
    if (isDisabled()) {
      return null;
    }
    person.passport = passportInfo;
    if (userIndex < selectedPassengers.length - 1) {
      updatePassengerWithPassport(person);
      setPassportInfo(selectedPassengers[userIndex + 1].passport);
      setUserIndex(userIndex + 1);
    }
    if (userIndex === selectedPassengers.length - 1) {
      updatePassengerWithPassport(person, true);
      setPassportInfo(undefined);
      sendToPassengerMachine(Event.CLEAR_PASSENGER_INFORMATION_ERROR);
    }
  };

  const getMessage = () => {
    switch (childState) {
      case PassportState.update:
      case PassportState.updateAndContinue:
        return t("passportInfo.saveTraveler");
      case PassportState.validate:
        return t("savingTraveler");
    }
  };

  const clearError = () => send(Event.CLEAR_PASSPORT_ERROR);

  const searchAgain = () => {
    clearError();
    history.push(PATH_FLIGHTS_SEARCH);
  };

  if (isLoading) {
    return (
      <B2BLoadingPopup
        open={isLoading}
        fullScreen
        message={getMessage()}
        popupSize={"mobile"}
      />
    );
  }

  if (passportError) {
    return (
      <ErrorModal
        open={Boolean(passportError)}
        title={t("checkoutError.genericErrorTitle")}
        subtitle={t("checkoutError.genericErrorSubtitle")}
        primaryButtonText={t("checkoutError.searchAgain")}
        primaryOnClick={searchAgain}
        image={Error}
      />
    );
  }

  const renderBackButton = () => {
    return (
      <BackButton
        className="passport-form-popup-back-button"
        onClick={handleGoBack}
      />
    );
  };

  const content = (
    <>
      <PassportInfoForm
        isMobile={true}
        title={t("passportInfo.title", {
          total: selectedPassengers?.length,
          currentIndex: userIndex + 1,
        })}
        subtitle={t("passportInfo.subtitle")}
        subtitle2={t("firstAndLastName", {
          firstName: selectedPassengers[userIndex]?.givenName,
          lastName: selectedPassengers[userIndex]?.surname,
        })}
        passportInfo={passportInfo}
        onPassportInfoChange={(info: Passport) => setPassportInfo(info)}
        passportExpiryError={passportExpiryErrorMessage}
        setPassportExpiryError={setPassportExpiryErrorMessage}
        passportNumberError={passportNumberErrorMessage}
        setPassportNumberError={setPassportNumberErrorMessage}
        departureDate={departureDate}
        returnDate={returnDate}
      />
      <MobileFloatingButton
        className="save-passport-button"
        disabled={isDisabled()}
        onClick={handleAddPassportInfo}
      >
        {t("save")}
      </MobileFloatingButton>
    </>
  );

  return matchesMobile ? (
    <MobileLayout
      className={clsx("mobile", "checkout-passport-information")}
      title={t("passportInfo.passport")}
      topLeftButton={renderBackButton()}
      headerTitle={t("passportInfo.passport")}
    >
      {content}
    </MobileLayout>
  ) : (
    <DesktopLayout
      clientLogo={clientAssets?.logo}
      rightContent={<ConnectedPriceBreakdownContent />}
    >
      {content}
    </DesktopLayout>
  );
};
